/**
 * LeadsCardsView — Oct 2026 · R293
 * Card-grid alternative to the AdvancedTable. Used by the Leads list
 * toolbar toggle. `compact` = denser two-line cards.
 */
import { useMemo } from "react";
import { Briefcase, Phone, Mail, User as UserIcon, Flame, MessageCircle } from "lucide-react";
import CallButton from "@/components/CallButton";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = {
  new: "border-l-sky-500",
  qualified: "border-l-emerald-500",
  proposal: "border-l-indigo-500",
  negotiation: "border-l-amber-500",
  won: "border-l-emerald-600 bg-emerald-50/30",
  lost: "border-l-rose-500 bg-rose-50/20",
};

export default function LeadsCardsView({
  rows = [], loading = false, compact = false, members = [],
  onRowClick, emptyTitle = "No leads", emptyDescription = "",
}) {
  const memberMap = useMemo(() => {
    const m = {};
    (members || []).forEach((x) => { m[x.user_id || x.id] = x.name || x.email; });
    return m;
  }, [members]);

  if (loading && rows.length === 0) {
    return (
      <div className="border border-border bg-background p-8 text-center text-sm text-muted-foreground" data-testid="leads-cards-loading">
        Loading leads…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-border bg-background p-10 text-center" data-testid="leads-cards-empty">
        <Briefcase className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-60" />
        <div className="text-sm font-semibold mb-1">{emptyTitle}</div>
        {emptyDescription ? <div className="text-xs text-muted-foreground">{emptyDescription}</div> : null}
      </div>
    );
  }
  const grid = compact
    ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-2"
    : "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3";
  return (
    <div className={grid} data-testid={compact ? "leads-cards-compact" : "leads-cards"}>
      {rows.map((l) => {
        const status = String(l.status || "new").toLowerCase();
        const borderClass = STATUS_COLORS[status] || "border-l-slate-400";
        const ownerName = memberMap[l.owner_user_id] || "Unassigned";
        const phone = l.phone || l.mobile || l.office_phone;
        const hot = String(l.temperature_manual || l.temperature_ai || "").toLowerCase() === "hot";
        return (
          <div
            key={l.id}
            className={`border-l-4 ${borderClass} border border-l-4 border-border bg-background p-3 cursor-pointer hover:bg-muted/20 active:bg-muted/40 transition-colors`}
            onClick={() => onRowClick?.(l)}
            data-testid={`lead-card-${l.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate flex items-center gap-1.5" data-testid={`lead-card-${l.id}-name`}>
                  {l.name || "—"}
                  {hot && <Flame className="w-3.5 h-3.5 text-rose-500 shrink-0" />}
                </div>
                {l.company ? (
                  <div className="text-xs text-muted-foreground truncate">{l.company}{l.title ? ` · ${l.title}` : ""}</div>
                ) : l.title ? (
                  <div className="text-xs text-muted-foreground truncate">{l.title}</div>
                ) : null}
              </div>
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-border bg-muted/40 whitespace-nowrap">
                {l.status || "new"}
              </span>
            </div>
            {!compact && (
              <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground">
                <UserIcon className="w-3 h-3" />
                <span className="truncate">{ownerName}</span>
                {l.next_followup_at && (
                  <>
                    <span className="opacity-40">·</span>
                    <span className="truncate">Next: {String(l.next_followup_at).slice(0, 10)}</span>
                  </>
                )}
              </div>
            )}
            <div className="mt-2 flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              {phone ? (
                <CallButton
                  phone={phone}
                  customer_name={l.name}
                  entity_type="lead"
                  entity_id={l.id}
                  entity_name={l.name || l.company}
                  className="rounded-none h-7 px-2 text-[11px]"
                  testid={`lead-card-${l.id}-call`}
                />
              ) : null}
              {phone && (
                <a
                  href={`https://wa.me/${String(phone).replace(/[^\d+]/g, "").replace(/^\+/, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-7 px-2 rounded-none border border-border text-[11px] hover:bg-muted/40"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`lead-card-${l.id}-wa`}
                  title="WhatsApp"
                >
                  <MessageCircle className="w-3 h-3" />
                </a>
              )}
              {l.email ? (
                <a
                  href={`mailto:${l.email}`}
                  className="inline-flex items-center justify-center h-7 px-2 rounded-none border border-border text-[11px] hover:bg-muted/40"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`lead-card-${l.id}-email`}
                  title={l.email}
                >
                  <Mail className="w-3 h-3" />
                </a>
              ) : null}
              <Button
                size="sm"
                variant="outline"
                className="ml-auto h-7 px-2 rounded-none text-[11px]"
                onClick={(e) => { e.stopPropagation(); onRowClick?.(l); }}
                data-testid={`lead-card-${l.id}-open`}
                aria-label="Open lead details"
              >
                Open
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
